import type { SortDirection } from "@simplicity/ui";
import {
  AdminTableContainer,
  AdminTableHeader,
  Button,
  FormError,
  Input,
  SectionHeading,
  SortableColumnHeader,
} from "@simplicity/ui";
import { Plus, X } from "lucide-react";
import { useState } from "react";

type FieldSortColumn = "name" | "alias";

function FieldRow({
  field,
  alias,
  onAliasChange,
  onRemove,
}: {
  field: string;
  alias: string;
  onAliasChange: (alias: string) => void;
  onRemove: () => void;
}) {
  return (
    <tr>
      <td className="px-3 py-1">
        <span className="text-xs">{field}</span>
      </td>
      <td className="px-3 py-1">
        <Input
          type="text"
          value={alias}
          onChange={(e) => onAliasChange(e.target.value)}
          placeholder="—"
          className="h-7 border-none bg-transparent px-0 font-mono text-xs shadow-none focus-visible:ring-0"
        />
      </td>
      <td className="px-1 py-1">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="hover:bg-destructive/20"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </Button>
      </td>
    </tr>
  );
}

function FieldsTableHeaderRow({
  sortField,
  sortDir,
  onToggle,
}: {
  sortField: FieldSortColumn;
  sortDir: SortDirection;
  onToggle: (c: FieldSortColumn) => void;
}) {
  return (
    <AdminTableHeader>
      <tr>
        <th>
          <SortableColumnHeader
            active={sortField === "name"}
            direction={sortDir}
            onClick={() => onToggle("name")}
          >
            API field name
          </SortableColumnHeader>
        </th>
        <th>
          <SortableColumnHeader
            active={sortField === "alias"}
            direction={sortDir}
            onClick={() => onToggle("alias")}
          >
            Column name in BQ
          </SortableColumnHeader>
        </th>
        <th className="w-8" />
      </tr>
    </AdminTableHeader>
  );
}

function getSortedFields(
  fields: string[],
  aliases: Record<string, string>,
  sortField: FieldSortColumn,
  sortDir: SortDirection,
): string[] {
  return [...fields].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "name") return dir * a.localeCompare(b, undefined, { sensitivity: "base" });
    const aliasA = aliases[a] ?? "";
    const aliasB = aliases[b] ?? "";
    if (!aliasA && aliasB) return 1;
    if (aliasA && !aliasB) return -1;
    return dir * aliasA.localeCompare(aliasB, undefined, { sensitivity: "base" });
  });
}

interface FieldsTableProps {
  fields: string[];
  aliases: Record<string, string>;
  onFieldsChange: (fields: string[]) => void;
  onAliasesChange: (aliases: Record<string, string>) => void;
}

export function FieldsTable({
  fields,
  aliases,
  onFieldsChange,
  onAliasesChange,
}: FieldsTableProps) {
  const [newField, setNewField] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<FieldSortColumn>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  function addField(raw: string) {
    const value = raw.trim();
    if (!value) return;
    if (fields.some((f) => f.toLowerCase() === value.toLowerCase())) {
      setError(`"${value}" already exists`);
      return;
    }
    onFieldsChange([...fields, value]);
    setNewField("");
    setError(null);
  }

  function removeField(fieldName: string) {
    onFieldsChange(fields.filter((f) => f !== fieldName));
    if (fieldName in aliases) {
      const next = { ...aliases };
      delete next[fieldName];
      onAliasesChange(Object.keys(next).length > 0 ? next : {});
    }
  }

  function updateAlias(fieldName: string, alias: string) {
    const next = { ...aliases };
    if (alias) {
      next[fieldName] = alias;
    } else {
      delete next[fieldName];
    }
    onAliasesChange(Object.keys(next).length > 0 ? next : {});
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addField(newField);
    }
  }

  function toggleSort(column: FieldSortColumn) {
    if (sortField === column) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(column);
      setSortDir("asc");
    }
  }

  const sortedFields = getSortedFields(fields, aliases, sortField, sortDir);

  return (
    <div>
      <SectionHeading className="mb-2 block">Fields</SectionHeading>
      <AdminTableContainer aria-label="Fields" style={{ borderRadius: 0 }}>
        <div className="max-h-[320px] overflow-y-auto">
          <table className="admin-table">
            <FieldsTableHeaderRow sortField={sortField} sortDir={sortDir} onToggle={toggleSort} />
            <tbody>
              {sortedFields.map((field) => (
                <FieldRow
                  key={field}
                  field={field}
                  alias={aliases[field] ?? ""}
                  onAliasChange={(alias) => updateAlias(field, alias)}
                  onRemove={() => removeField(field)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {/* Add field row */}
        <div className="flex items-center gap-2 border-t border-border/50 px-3 py-1.5">
          <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />
          <Input
            type="text"
            value={newField}
            onChange={(e) => {
              setNewField(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (newField.trim()) addField(newField);
            }}
            placeholder="Add field..."
            className="h-7 border-none bg-transparent px-0 font-mono text-xs shadow-none focus-visible:ring-0"
          />
        </div>
      </AdminTableContainer>
      {error && <FormError>{error}</FormError>}
    </div>
  );
}
